import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowDownCircle, ArrowRight, ChevronUp, Mail, MessageCircle, Send, Sparkles, Users, X } from 'lucide-react';
import { buildWhatsappLink, normalizePhoneForWhatsapp } from '../../mvp/features/public/utils/whatsapp';
import { trackMvpEvent } from '../../mvp/features/public/utils/tracking';

type ChatFunnelContext = {
  enabled?: boolean;
  widget?: {
    enabled?: boolean;
    style?: string;
    position?: 'bottom-right' | 'bottom-left';
    buttonLabel?: string;
    popupTitle?: string;
    popupSubtitle?: string;
    welcomeMessage?: string;
    supportLabel?: string;
    supportMessage?: string;
    avatarUrl?: string;
    accentColor?: string;
    pulse?: boolean;
    showOnPaths?: string[];
    hideOnPaths?: string[];
    delaySeconds?: number;
    scrollPercent?: number;
  };
  contactChannels?: Array<{ type?: string; label?: string; value?: string; isPrimary?: boolean; isActive?: boolean }>;
  preMessages?: Array<{ key?: string; label?: string; message?: string; isActive?: boolean }>;
  supportAgents?: Array<{ _id?: string; name?: string; role?: string; avatarUrl?: string; isActive?: boolean }>;
  selectedAgent?: { _id?: string; name?: string; role?: string; avatarUrl?: string } | null;
  activeTemplate?: { key?: string; title?: string; message?: string; buttonLabel?: string; channel?: string } | null;
  activeRule?: { _id?: string; name?: string; note?: string; channel?: string } | null;
  whatsapp?: { phone?: string; message?: string; href?: string };
  routing?: { ticketSupport24h?: boolean; mode?: string };
  leadCapture?: { enabled?: boolean; formTitle?: string; formSubtitle?: string; privacyNote?: string; collectEmail?: boolean; collectPhone?: boolean; collectCountry?: boolean };
  analytics?: { ga4Enabled?: boolean; gtmEnabled?: boolean; eventPrefix?: string };
  partnerProgram?: { enabled?: boolean; label?: string; description?: string; ctaLabel?: string; ctaHref?: string; commissionNote?: string };
};

type LeadForm = {
  name: string;
  email: string;
  phone: string;
  message: string;
};

const DEFAULT_WHATSAPP_MESSAGE = 'Hello Pearl Voyages, I would like help planning a trip.';
const SITE_NAME = 'Pearl Voyages';

const isLegacyPopupTitle = (value?: string) => String(value || '').toLowerCase().includes('whatsapp') && String(value || '').toLowerCase().includes('chat');

const isLegacyWelcomeMessage = (value?: string) =>
  String(value || '').toLowerCase().includes('plan your next trip') && String(value || '').toLowerCase().includes('help');

const normalizeWhatsappButtonLabel = (value?: string) => {
  const label = String(value || '').trim();
  return !label || label.toLowerCase() === 'start chat' ? 'Chat on WhatsApp' : label;
};

const getLocaleFromPath = (pathname: string) => {
  const match = pathname.match(/^\/([a-z]{2})(?:\/|$)/i);
  return match?.[1]?.toLowerCase() || '';
};

const getDeviceType = () => {
  if (typeof window === 'undefined') return 'desktop';
  return window.innerWidth < 768 ? 'mobile' : 'desktop';
};

const firstUsablePhone = (...values: Array<string | undefined>) =>
  values.find((value) => normalizePhoneForWhatsapp(value || '').length > 0) || '';

const getCurrentPageTitle = () => {
  if (typeof document === 'undefined') return SITE_NAME;
  return document.title?.trim() || SITE_NAME;
};

const getCurrentPageUrl = () => {
  if (typeof window === 'undefined') return '';
  return window.location.href;
};

const buildContextMessage = (presetMessage: string, editedMessage?: string) => {
  const finalMessage = String(editedMessage || '').trim() || String(presetMessage || DEFAULT_WHATSAPP_MESSAGE).trim();
  const pageTitle = getCurrentPageTitle();
  const pageUrl = getCurrentPageUrl();

  return [
    `Hello ${SITE_NAME},`,
    '',
    'I am viewing:',
    pageTitle,
    pageUrl,
    '',
    'Message:',
    finalMessage,
  ]
    .filter((line, index, lines) => line || lines[index - 1] !== '')
    .join('\n');
};

const buildMailtoLink = (email: string, subject: string, body: string) =>
  `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

const postEvent = async (eventName: string, payload: Record<string, unknown>) => {
  try {
    await fetch('/api/chatfunnel/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        eventName,
        payload,
        path: window.location.pathname,
        locale: getLocaleFromPath(window.location.pathname),
        device: getDeviceType(),
      }),
    });
  } catch {
    // Non-blocking.
  }
};

const pushAnalytics = (eventName: string, payload: Record<string, unknown>) => {
  if (typeof window === 'undefined') return;
  const win = window as Window & { gtag?: (...args: any[]) => void; dataLayer?: any[] };

  if (typeof win.gtag === 'function') {
    win.gtag('event', eventName, payload);
  }

  win.dataLayer = win.dataLayer || [];
  win.dataLayer.push({ event: eventName, ...payload });
};

const Field = ({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) => (
  <label className="block space-y-1.5">
    <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</span>
    {children}
    {hint ? <span className="block text-xs leading-5 text-slate-400">{hint}</span> : null}
  </label>
);

const QuickPill = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all',
      active
        ? 'border-emerald-200 bg-emerald-50 text-emerald-800 shadow-sm'
        : 'border-slate-200 bg-white text-slate-600 hover:border-emerald-200 hover:text-emerald-700',
    ].join(' ')}
  >
    <Sparkles className="h-3.5 w-3.5" />
    {label}
  </button>
);

export default function FloatingWhatsApp() {
  const location = useLocation();
  const siteSettings = useSelector((state: any) => state.siteSettings?.settings || {});
  const [context, setContext] = useState<ChatFunnelContext | null>(null);
  const [open, setOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [ready, setReady] = useState(false);
  const [selectedKey, setSelectedKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [leadForm, setLeadForm] = useState<LeadForm>({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const loadContext = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          path: location.pathname,
          locale: getLocaleFromPath(location.pathname),
          device: getDeviceType(),
        });
        const response = await fetch(`/api/chatfunnel/context?${params.toString()}`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('context fetch failed');
        const data = await response.json();
        if (!cancelled) {
          setContext(data.context || null);
          const firstMessage = data.context?.preMessages?.find((item: any) => item?.isActive)?.key || data.context?.activeTemplate?.key || '';
          setSelectedKey(firstMessage);
          setReady(false);
        }
      } catch {
        if (!cancelled) setContext(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadContext();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!context?.widget?.enabled) return undefined;
    const showOnPaths = context.widget.showOnPaths || [];
    const hideOnPaths = context.widget.hideOnPaths || [];

    const shouldHide =
      (hideOnPaths || []).some((pattern) => location.pathname.toLowerCase().includes(String(pattern).trim().toLowerCase())) ||
      (showOnPaths.length > 0 && !showOnPaths.some((pattern) => location.pathname.toLowerCase().includes(String(pattern).trim().toLowerCase())));

    if (shouldHide) return undefined;

    const delay = Math.max(Number(context.widget.delaySeconds || 0), 0) * 1000;
    const scrollThreshold = Math.max(Number(context.widget.scrollPercent || 0), 0);
    let timeoutId: number | undefined;

    const reveal = () => setReady(true);
    timeoutId = window.setTimeout(reveal, delay);

    const onScroll = () => {
      if (ready) return;
      const total = document.documentElement.scrollHeight - window.innerHeight;
      if (total <= 0) {
        reveal();
        return;
      }
      const progress = (window.scrollY / total) * 100;
      if (progress >= scrollThreshold) reveal();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      window.removeEventListener('scroll', onScroll);
    };
  }, [context, location.pathname, ready]);

  useEffect(() => {
    if (!open) return;
    trackMvpEvent('chatfunnel_widget_open', { path: location.pathname });
    pushAnalytics('chatfunnel_widget_open', {
      page_path: location.pathname,
      event_category: 'chatfunnel',
      event_label: 'open',
    });
    void postEvent('widget.open', { path: location.pathname });
  }, [open, location.pathname]);

  const selectedMessage = useMemo(() => {
    const preMessage = (context?.preMessages || []).find((item) => item?.key === selectedKey && item?.isActive);
    return (
      preMessage?.message ||
      context?.activeTemplate?.message ||
      siteSettings?.whatsappMessage ||
      context?.whatsapp?.message ||
      DEFAULT_WHATSAPP_MESSAGE
    ).trim();
  }, [context, selectedKey, siteSettings?.whatsappMessage]);

  const whatsappChannelPhone = (context?.contactChannels || []).find((channel) => channel?.type === 'whatsapp' && channel?.isActive !== false)?.value;
  const emailChannel = (context?.contactChannels || []).find((channel) => channel?.type === 'email' && channel?.isActive !== false)?.value;
  const phone = firstUsablePhone(context?.whatsapp?.phone, whatsappChannelPhone, siteSettings?.supportPhone, '+46704683005');
  const finalConversationMessage = useMemo(
    () => buildContextMessage(selectedMessage || DEFAULT_WHATSAPP_MESSAGE, leadForm.message),
    [leadForm.message, selectedMessage]
  );
  const supportEmail = emailChannel || siteSettings?.supportEmail || 'info@pearlvoyages.com';
  const whatsappHref = buildWhatsappLink(phone, finalConversationMessage);
  const emailHref = buildMailtoLink(supportEmail, `Pearl Voyages inquiry: ${getCurrentPageTitle()}`, finalConversationMessage);
  const buttonLabel = normalizeWhatsappButtonLabel(context?.widget?.buttonLabel || siteSettings?.whatsappLabel);
  const triggerLabel = buttonLabel === 'Chat on WhatsApp' ? 'Plan by WhatsApp' : buttonLabel;
  const activeTemplateButtonLabel = normalizeWhatsappButtonLabel(context?.activeTemplate?.buttonLabel);
  const accentColor = context?.widget?.accentColor || '#16a34a';
  const positionClass = context?.widget?.position === 'bottom-left' ? 'left-5' : 'right-5';

  const shouldRender =
    ready &&
    context?.enabled !== false &&
    context?.widget?.enabled !== false &&
    !(context?.widget?.hideOnPaths || []).some((pattern) => location.pathname.toLowerCase().includes(String(pattern).trim().toLowerCase()));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const win = window as Window & { __PEARL_CHATFUNNEL_GLOBAL__?: boolean };

    if (!shouldRender) {
      delete win.__PEARL_CHATFUNNEL_GLOBAL__;
      return undefined;
    }

    win.__PEARL_CHATFUNNEL_GLOBAL__ = true;
    return () => {
      delete win.__PEARL_CHATFUNNEL_GLOBAL__;
    };
  }, [shouldRender]);

  const handleOpen = () => {
    setHasInteracted(true);
    setOpen(true);
    trackMvpEvent('whatsapp_click', { source: 'chatfunnel_widget_open', path: location.pathname });
    pushAnalytics('chatfunnel_widget_click', {
      page_path: location.pathname,
      event_category: 'chatfunnel',
      event_label: 'open',
    });
    void postEvent('widget.click', { action: 'open', path: location.pathname });
  };

  const handleClose = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    setOpen(false);
    void postEvent('widget.close', { path: location.pathname });
  };

  const handleMessageClick = (key: string, label: string) => {
    setHasInteracted(true);
    setSelectedKey(key);
    trackMvpEvent('whatsapp_click', { source: 'chatfunnel_quick_message', key, label });
    pushAnalytics('chatfunnel_quick_message', {
      page_path: location.pathname,
      event_category: 'chatfunnel',
      event_label: key,
    });
    void postEvent('widget.message_select', { key, label, path: location.pathname });
  };

  const handleSendEmailInquiry = async () => {
    setHasInteracted(true);
    setSubmitting(true);
    try {
      const payload = {
        source: 'widget',
        name: leadForm.name,
        email: leadForm.email,
        phone: leadForm.phone,
        message: finalConversationMessage,
        channel: 'email',
        templateKey: context?.activeTemplate?.key || selectedKey || '',
        path: location.pathname,
        locale: getLocaleFromPath(location.pathname),
        device: getDeviceType(),
        consent: true,
        meta: {
          pageTitle: document.title,
          referrer: document.referrer || '',
        },
      };

      try {
        await fetch('/api/chatfunnel/leads', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } catch {
        // The email handoff should still work if lead capture is temporarily unavailable.
      }

      trackMvpEvent('chatfunnel_email_inquiry', { source: 'chatfunnel_lead_submit', path: location.pathname });
      pushAnalytics('chatfunnel_lead_submit', {
        page_path: location.pathname,
        event_category: 'chatfunnel',
        event_label: 'email_inquiry',
      });
      void postEvent('lead.created', payload);

      window.open(emailHref, '_blank', 'noopener,noreferrer');
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (!shouldRender || loading) return null;

  const popupTitle = context?.widget?.popupTitle && !isLegacyPopupTitle(context.widget.popupTitle) ? context.widget.popupTitle : '';
  const popupMessage =
    context?.widget?.welcomeMessage && !isLegacyWelcomeMessage(context.widget.welcomeMessage)
      ? context.widget.welcomeMessage
      : context?.activeTemplate?.subtitle || 'Public visitors can choose a message and continue in WhatsApp.';

  return (
    <div className={`fixed bottom-4 ${positionClass} z-[90] flex max-h-[calc(100svh-1.5rem)] flex-col items-end gap-3 sm:bottom-5`}>
      {open ? (
        <div className="flex max-h-[min(78svh,560px)] w-[min(92vw,380px)] flex-col overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.22)] ring-1 ring-black/5">
          <div className="relative shrink-0 overflow-hidden px-4 py-3.5 text-white" style={{ background: `linear-gradient(135deg, ${accentColor}, #0f766e)` }}>
            <div className="absolute -right-10 -top-10 h-36 w-36 rounded-full bg-white/10 blur-3xl" />
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/20">
                  <MessageCircle className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  {popupTitle ? <p className="text-sm font-semibold">{popupTitle}</p> : null}
                  {popupMessage ? <p className="mt-1 text-sm leading-5 text-white/90">{popupMessage}</p> : null}
                </div>
                <button
                type="button"
                onClick={handleClose}
                onMouseDown={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
                className="relative z-10 shrink-0 rounded-full bg-white/10 p-2 text-white/90 transition hover:bg-white/20"
                aria-label="Close WhatsApp widget"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain p-3.5 sm:p-4">
            {(context?.preMessages || []).filter((item) => item?.isActive).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {(context?.preMessages || []).filter((item) => item?.isActive).map((item) => (
                  <QuickPill
                    key={item.key}
                    label={item.label || item.key || 'Quick reply'}
                    active={selectedKey === item.key}
                    onClick={() => handleMessageClick(item.key || '', item.label || item.key || '')}
                  />
                ))}
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-3">
              {context?.selectedAgent ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-2.5">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-emerald-100 text-emerald-700">
                      {context.selectedAgent.avatarUrl ? (
                        <img src={context.selectedAgent.avatarUrl} alt={context.selectedAgent.name || 'Support agent'} className="h-full w-full object-cover" />
                      ) : (
                        <Users className="h-4 w-4" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{context.selectedAgent.name || 'Support agent'}</p>
                      <p className="text-xs text-slate-500">{context.selectedAgent.role || 'Ready to help'}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-2.5">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{context?.widget?.supportLabel || 'Support'}</p>
                  <p className="mt-1 text-sm leading-5 text-slate-700">{context?.widget?.supportMessage || 'We usually reply through WhatsApp within business hours.'}</p>
                </div>
              )}

            </div>

            <div className="grid grid-cols-1 gap-3">
              <Field label="Message">
                <textarea
                  value={leadForm.message}
                  onChange={(e) => setLeadForm((current) => ({ ...current, message: e.target.value }))}
                  className="input min-h-[76px] w-full resize-y py-2 pl-1 pr-2 text-sm leading-5"
                  placeholder={selectedMessage}
                />
              </Field>
              <Field label="Your name">
                <input
                  value={leadForm.name}
                  onChange={(e) => setLeadForm((current) => ({ ...current, name: e.target.value }))}
                  className="input w-full py-2 pl-1 pr-2 text-sm"
                  placeholder="Full name"
                />
              </Field>
            </div>

            <details className="group rounded-2xl border border-[#064239]/15 bg-white">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2 text-sm font-semibold text-[#064239] marker:hidden">
                <span>Add contact details</span>
                <ArrowDownCircle className="h-4 w-4 shrink-0 text-[#064239] transition-transform duration-200 group-open:rotate-180" />
              </summary>
              <div className="grid grid-cols-1 gap-3 border-t border-slate-100 p-3">
                <Field label="Phone / WhatsApp">
                  <input
                    value={leadForm.phone}
                    onChange={(e) => setLeadForm((current) => ({ ...current, phone: e.target.value }))}
                    className="input w-full py-2 pl-1 pr-2 text-sm"
                    placeholder="+256..."
                  />
                </Field>
                <Field label="Email">
                  <input
                    value={leadForm.email}
                    onChange={(e) => setLeadForm((current) => ({ ...current, email: e.target.value }))}
                    className="input w-full py-2 pl-1 pr-2 text-sm"
                    placeholder="name@email.com"
                  />
                </Field>
              </div>
            </details>

            {context?.partnerProgram?.enabled ? (
              <a
                href={context.partnerProgram.ctaHref || '/partner-program'}
                className="flex items-center justify-between rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm font-medium text-amber-900 transition hover:bg-amber-100"
                onClick={() => {
                  trackMvpEvent('chatfunnel_partner_click', { path: location.pathname });
                  pushAnalytics('chatfunnel_partner_click', {
                    page_path: location.pathname,
                    event_category: 'chatfunnel',
                    event_label: context.partnerProgram?.ctaLabel || 'partner',
                  });
                }}
              >
                <span>
                  <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">{context.partnerProgram.label || 'Partner Program'}</span>
                  <span className="block text-sm leading-5">{context.partnerProgram.description || 'Join the partner program and send qualified travel leads.'}</span>
                </span>
                <ArrowRight className="h-4 w-4" />
              </a>
            ) : null}
          </div>

          <div className="shrink-0 border-t border-slate-200 bg-white p-2.5 sm:p-3">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => window.open(whatsappHref, '_blank', 'noopener,noreferrer')}
                className="inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full bg-emerald-600 px-2.5 py-2.5 text-[11px] font-semibold text-white transition hover:bg-emerald-700 sm:gap-2 sm:px-4 sm:text-sm"
              >
                <Send className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                <span className="whitespace-nowrap">{activeTemplateButtonLabel || buttonLabel}</span>
              </button>
              <button
                type="button"
                onClick={handleSendEmailInquiry}
                disabled={submitting}
                className="inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-2.5 text-[11px] font-semibold text-slate-700 transition hover:border-emerald-200 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-60 sm:gap-2 sm:px-4 sm:text-sm"
              >
                <Mail className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                <span className="whitespace-nowrap">{submitting ? 'Sending...' : 'Send Email Inquiry'}</span>
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => {
          if (open) {
            handleClose();
            return;
          }
          handleOpen();
          setOpen(true);
          setReady(true);
        }}
        onPointerEnter={() => setHasInteracted(true)}
        onFocus={() => setHasInteracted(true)}
        className="group inline-flex items-center gap-2 rounded-full bg-[#064239] px-4 py-3 text-white shadow-[0_18px_45px_rgba(6,66,57,0.28)] ring-1 ring-white/20 transition-all duration-300 hover:-translate-y-1 hover:bg-[#075347] focus:outline-none focus:ring-4 focus:ring-[#D9B56D]/30 sm:gap-3 sm:px-5"
        style={{
          background: open ? '#06362f' : undefined,
        }}
        aria-label={triggerLabel}
      >
        <span
          className="relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-white/12 text-white ring-1 ring-white/20 transition-all duration-300 sm:h-10 sm:w-10"
        >
          {!hasInteracted && !open ? (
            <>
              <span className="absolute inset-0 rounded-full bg-white/10 animate-heartbeat" />
              <span className="absolute inset-0 rounded-full bg-[#D9B56D]/10 animate-heartbeat" style={{ animationDelay: '0.6s' }} />
            </>
          ) : null}
          <MessageCircle className="relative z-10 h-5 w-5" />
        </span>
        <span className="flex max-w-[10.5rem] flex-col items-start text-left leading-none">
          <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-[#D9B56D]">WhatsApp</span>
          <span className="mt-1 block text-sm font-semibold leading-4">{triggerLabel}</span>
        </span>
        <ChevronUp className={`hidden h-4 w-4 text-white/75 transition-transform sm:block ${open ? 'rotate-180' : ''}`} />
      </button>
    </div>
  );
}

